import React, { FC, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { ImpulseSpinner } from '~Components/ImpulseSpinner';
import styled from '@emotion/styled';
import { addToast } from '~/actions';

import { useI18n, useSelector } from '~Hooks';

import { handyBreakpoint } from '~Styles/breakpoints';
import RadioIcon, { OptionButton } from '~Components/RadioOptions';
import { GET_LAYOUTS } from '~Scenes/Onboarding/api/queries';
import { GetLayouts } from '~Scenes/Onboarding/api/__queries__';

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  justify-content: center;
  overflow: hidden;
`;

const LayoutContainer = styled.div<{ isOnboarding?: boolean }>`
  border: ${({ isOnboarding }) =>
    isOnboarding ? '1px solid var(--colorBlueGray1000)' : '1px solid rgba(0, 0, 0, 0.7)'};
  border-radius: 4px;
  margin: 0 48px;
  display: flex;
  flex-direction: column;
  overflow: scroll;
  flex: 1;

  ${handyBreakpoint} {
    width: 100%;
  }
`;

const Layout = styled.div<{ isOnboarding?: boolean }>`
  font-size: 20px;
  text-overflow: ellipsis;
  color: ${({ isOnboarding }) => (isOnboarding ? 'var(--colorWhite)' : 'initial')};
`;

interface Props {
  isOnboarding?: boolean;
}

const LayoutPos: FC<Props> = ({ isOnboarding }) => {
  const venueId = useSelector((state) => state.venueId);
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const POSLayout = useSelector((state) => state.POSLayout);

  const layoutsQuery = useQuery<GetLayouts>(GET_LAYOUTS, {
    variables: { venueId },
    skip: !venueId,
  });

  const layouts = layoutsQuery.data?.venue?.company?.layouts ?? [];

  /**
   * Store layouts to compare at a later time.
   * Will show option to select layout if they're changed.
   */
  useEffect(() => {
    if (layoutsQuery.data?.venue) {
      const POSLayoutIds = layouts.map((layout) => layout.id);
      dispatch({
        type: 'SET_POS_LAYOUTS',
        POSLayouts: POSLayoutIds,
      });
      window.postMessageBridge({
        POSLayouts: POSLayoutIds,
      });
    }
  }, [layoutsQuery.data, layouts]);

  if (layoutsQuery.loading) {
    return (
      <Wrapper>
        <ImpulseSpinner size={64} />
      </Wrapper>
    );
  }

  const updatePOSLayout = (layout: string) => {
    dispatch({
      type: 'SET_POS_LAYOUT',
      POSLayout: layout,
    });
    window.postMessageBridge({
      devicePOSLayout: layout,
    });

    dispatch(addToast(i18n('TOAST.Layout.Updated'), true));
  };

  return (
    <Wrapper>
      <LayoutContainer isOnboarding={isOnboarding}>
        <OptionButton selected={POSLayout === 'NONE'} onTouchStart={() => updatePOSLayout('NONE')}>
          <RadioIcon active={POSLayout === 'NONE'} />
          <Layout isOnboarding={isOnboarding} onTouchStart={() => updatePOSLayout('NONE')}>
            {i18n('Settings.LayoutNone')} <i>{i18n('Settings.LayoutUseCategories')}</i>
          </Layout>
        </OptionButton>

        {layouts.map((layout) => (
          <OptionButton
            selected={layout.id === POSLayout}
            onTouchStart={() => updatePOSLayout(layout.id)}>
            <RadioIcon active={layout.id === POSLayout} />
            <Layout key={layout.id} isOnboarding={isOnboarding}>
              {i18n(layout.name)}
            </Layout>
          </OptionButton>
        ))}
      </LayoutContainer>
    </Wrapper>
  );
};

export default LayoutPos;
